import { Stack, TextField, ToggleButton, Tooltip } from '@mui/material';
import {
  accountFilterOptions,
  toggleAccountFilter,
  type DescalceFilterState
} from './descalceFilters';
import type { OpenerDescalceAccountMeta } from '../../api/useOpenerDescalce';

interface DescalceFilterBarProps {
  filters: DescalceFilterState;
  onChange: (next: DescalceFilterState) => void;
  /** The fleet as the snapshot reports it — the ONLY source of the account
   *  vocabulary. No account is offered that the sweeper did not read. */
  accounts: OpenerDescalceAccountMeta[] | undefined;
}

const toggleSx = { fontSize: '0.65rem', py: 0.25, px: 1 };

const PLATFORM_PREFIX: Record<OpenerDescalceAccountMeta['platform'], string> = {
  kalshi: 'K',
  polymarket: 'P'
};

export const DescalceFilterBar = ({
  filters,
  onChange,
  accounts
}: DescalceFilterBarProps) => {
  const set = (patch: Partial<DescalceFilterState>) =>
    onChange({ ...filters, ...patch });

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      sx={{ mb: 1, rowGap: 1 }}
    >
      <TextField
        size="small"
        placeholder="Search group / market…"
        value={filters.search}
        onChange={e => set({ search: e.target.value })}
        sx={{
          width: 220,
          '& .MuiOutlinedInput-root': { fontSize: '0.75rem', height: 28 },
          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 }
        }}
        inputProps={{ 'aria-label': 'Search by group or market' }}
      />
      <TextField
        size="small"
        type="number"
        label="Min descalce"
        value={filters.minDescalce}
        onChange={e => set({ minDescalce: Number(e.target.value) || 0 })}
        sx={{
          width: 130,
          '& .MuiOutlinedInput-root': { fontSize: '0.75rem', height: 28 }
        }}
        inputProps={{ step: 0.5, min: 0, 'aria-label': 'Minimum descalce' }}
      />
      <Tooltip title="Only rows whose group has pods desired ON. ON by default — turn it off to include stopped and ungrouped rows.">
        <ToggleButton
          size="small"
          value="onlyGroupsOn"
          selected={filters.onlyGroupsOn}
          onChange={() => set({ onlyGroupsOn: !filters.onlyGroupsOn })}
          sx={toggleSx}
        >
          groups ON
        </ToggleButton>
      </Tooltip>
      {accountFilterOptions(accounts).map(({ key, account }) => (
        <Tooltip
          key={key}
          title={`Only rows on the ${account.platform} account "${account.accountId}". Selecting several keeps a row that touches ANY of them; selecting none scopes to nothing and shows every row.`}
        >
          <ToggleButton
            size="small"
            value={key}
            selected={filters.accounts.includes(key)}
            onChange={() =>
              set({ accounts: toggleAccountFilter(filters.accounts, key) })
            }
            data-account-filter={key}
            sx={toggleSx}
          >
            {`${PLATFORM_PREFIX[account.platform]}: ${account.accountId}`}
          </ToggleButton>
        </Tooltip>
      ))}
      <Tooltip title="Only rows carrying an unread triage card — a stuck counter on this rule, or an orphaned automation fill on this pair. Off by default. While the triage index is unreadable this hides everything, because nothing can be evaluated.">
        <ToggleButton
          size="small"
          value="onlyWithTriage"
          selected={filters.onlyWithTriage}
          onChange={() => set({ onlyWithTriage: !filters.onlyWithTriage })}
          sx={toggleSx}
        >
          ⚠ triage only
        </ToggleButton>
      </Tooltip>
      <Tooltip title="Only rows where the engine belief disagrees with the venue read">
        <ToggleButton
          size="small"
          value="onlyDivergence"
          selected={filters.onlyDivergence}
          onChange={() => set({ onlyDivergence: !filters.onlyDivergence })}
          sx={toggleSx}
        >
          divergence only
        </ToggleButton>
      </Tooltip>
    </Stack>
  );
};
